// Service pour communiquer avec le backend Stripe
// Configuration intelligente selon l'environnement
function getBackendUrl() {
  // PRIORITÉ 1: Variable d'environnement explicite (toujours utilisée si définie)
  // ⚠️ IMPORTANT: En production sur Netlify, vous DEVEZ définir REACT_APP_BACKEND_URL
  // dans les variables d'environnement Netlify pointant vers votre backend déployé
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log(`🔧 Backend URL depuis REACT_APP_BACKEND_URL: ${process.env.REACT_APP_BACKEND_URL}`);
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // PRIORITÉ 2: Détection automatique de l'environnement
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '';
  
  // En développement local (localhost:3000)
  if (isLocalhost) {
    const backendUrl = 'http://localhost:4242';
    console.log(`🔧 Backend URL (développement): ${backendUrl}`);
    return backendUrl;
  }
  
  // PRIORITÉ 3: Production - détection du domaine
  const hostname = window.location.hostname;
  const isProduction = hostname.includes('ferme-a-lancienne.com') || 
                      hostname.includes('lafermeancienne.com') ||
                      hostname.includes('netlify.app') ||
                      hostname.includes('vercel.app');
  
  if (isProduction) {
    // ⚠️ ATTENTION: Si vous voyez ce message en production, configurez REACT_APP_BACKEND_URL
    console.warn('⚠️ REACT_APP_BACKEND_URL non défini en production !');
    console.warn('⚠️ Le backend doit être déployé séparément (Heroku, Railway, Render, etc.)');
    console.warn('⚠️ Configurez REACT_APP_BACKEND_URL dans les variables d\'environnement (Vercel/Netlify)');
    
    // Tentative intelligente : utiliser le même domaine (ne fonctionnera que si le backend est déployé avec le frontend)
    // Pour Netlify/Vercel, cela échouera car ils ne servent que des fichiers statiques
    const productionUrl = window.location.origin;
    console.log(`🔧 Backend URL (production - tentative même domaine): ${productionUrl}`);
    console.warn('⚠️ Cela échouera si le backend n\'est pas déployé sur le même domaine !');
    return productionUrl;
  }
  
  // Fallback : même domaine
  const fallbackUrl = window.location.origin;
  console.log(`🔧 Backend URL (fallback): ${fallbackUrl}`);
  return fallbackUrl;
}

const BACKEND_URL = getBackendUrl();

// Log de la configuration au chargement
console.log('═'.repeat(60));
console.log('🔧 CONFIGURATION BACKEND STRIPE');
console.log('═'.repeat(60));
console.log(`📍 Environnement: ${process.env.NODE_ENV || 'development'}`);
console.log(`🌐 Hostname: ${window.location.hostname}`);
console.log(`🔗 URL Backend: ${BACKEND_URL}`);
console.log(`📦 Variable REACT_APP_BACKEND_URL: ${process.env.REACT_APP_BACKEND_URL || 'Non définie'}`);
console.log('═'.repeat(60));

class StripeService {
  // Synchroniser tous les produits avec Stripe et retourner les produits avec IDs Stripe
  async syncProducts(products) {
    try {
      console.log(`🔄 Synchronisation de ${products.length} produits avec Stripe...`);
      console.log(`🌐 URL: ${BACKEND_URL}/api/sync-products`);
      
      // Timeout de 45 secondes pour la synchronisation (peut être longue avec beaucoup de produits)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000);
      
      const response = await fetch(`${BACKEND_URL}/api/sync-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products }),
        signal: controller.signal
      }).finally(() => clearTimeout(timeoutId));

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue');
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ Synchronisation terminée:`, data);
      
      // Retourner les produits synchronisés avec leurs IDs Stripe
      return {
        ...data,
        syncedProducts: data.syncedProducts || []
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('❌ Timeout lors de la synchronisation (plus de 45 secondes)');
        throw new Error('La synchronisation prend trop de temps. Le serveur peut être surchargé.');
      }
      console.error('❌ Erreur lors de la synchronisation des produits:', error);
      throw error;
    }
  }

  // Synchroniser automatiquement tous les produits au démarrage
  async syncAllProducts(products) {
    try {
      console.log(`🚀 Synchronisation automatique de tous les produits avec Stripe...`);
      const result = await this.syncProducts(products);
      
      if (result.syncedProducts && result.syncedProducts.length > 0) {
        console.log(`✅ ${result.syncedProducts.length} produits synchronisés avec succès`);
        return result.syncedProducts;
      }
      
      return [];
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation automatique:', error);
      // Ne pas bloquer le démarrage si la synchronisation échoue
      return products; // Retourner les produits originaux
    }
  }

  // Vérifier si le serveur backend est accessible
  async checkServerHealth() {
    try {
      const response = await fetch(`${BACKEND_URL}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.ok;
    } catch (error) {
      console.error('❌ Serveur backend inaccessible:', error);
      return false;
    }
  }

  // Créer une session de paiement
  async createCheckoutSession(cartItems) {
    try {
      console.log('═'.repeat(60));
      console.log(`💳 CRÉATION D'UNE SESSION DE PAIEMENT`);
      console.log('═'.repeat(60));
      console.log(`📦 Articles: ${cartItems.length}`);
      console.log(`🌐 URL Backend: ${BACKEND_URL}`);
      console.log(`🔗 Endpoint: ${BACKEND_URL}/api/create-checkout-session`);
      console.log(`🌍 Environnement: ${process.env.NODE_ENV || 'development'}`);
      console.log(`📍 Hostname: ${window.location.hostname}`);
      console.log(`📦 Articles du panier:`, cartItems);
      
      // Vérifier d'abord si le serveur est accessible (uniquement pour localhost)
      if (BACKEND_URL.includes('localhost') || BACKEND_URL.includes('127.0.0.1')) {
        const isServerOnline = await this.checkServerHealth();
        if (!isServerOnline) {
          throw new Error(`Le serveur backend n'est pas accessible à l'adresse ${BACKEND_URL}.\n\nVeuillez démarrer le serveur backend avec la commande :\n  npm run server`);
        }
      }
      
      const url = `${BACKEND_URL}/api/create-checkout-session`;
      console.log(`🔗 Appel de: ${url}`);
      
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cartItems }),
        });
      } catch (fetchError) {
        console.error('❌ Erreur réseau lors du fetch:', fetchError);
        
        // Détecter différents types d'erreurs réseau
        if (fetchError.message.includes('Failed to fetch') || 
            fetchError.message.includes('NetworkError') ||
            fetchError.message.includes('Network request failed')) {
          throw new Error(`Impossible de contacter le serveur de paiement.\n\nCauses possibles :\n- Le serveur backend n'est pas démarré (exécutez : npm run server)\n- Le serveur n'écoute pas sur le port attendu\n- Problème de connexion réseau\n\nURL attendue: ${BACKEND_URL}`);
        }
        throw fetchError;
      }

      // Lire le body une seule fois
      let responseText;
      try {
        responseText = await response.text();
      } catch (textError) {
        console.error('❌ Erreur lors de la lecture de la réponse:', textError);
        throw new Error('Erreur lors de la lecture de la réponse du serveur.');
      }
      
      if (!response.ok) {
        let errorMessage = `Erreur HTTP: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
          console.error(`❌ Détails de l'erreur:`, errorData);
        } catch (parseError) {
          errorMessage = responseText || errorMessage;
          console.error(`❌ Erreur HTTP: ${response.status} - ${responseText}`);
        }
        throw new Error(errorMessage);
      }

      // Parser le JSON de la réponse
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Erreur lors du parsing JSON:', parseError);
        console.error('❌ Réponse reçue:', responseText);
        throw new Error('Réponse invalide du serveur.');
      }
      
      console.log(`✅ Session créée:`, data.sessionId);
      
      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la création de la session:', error);
      
      // Message d'erreur plus convivial
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('Network request failed')) {
        throw new Error(`Impossible de contacter le serveur de paiement.\n\nCauses possibles :\n- Le serveur backend n'est pas démarré (exécutez : npm run server)\n- Le serveur n'écoute pas sur le port attendu\n- Problème de connexion réseau\n\nURL attendue: ${BACKEND_URL}`);
      } else if (error.message.includes('502')) {
        throw new Error('Le service de paiement est temporairement indisponible. Veuillez réessayer dans quelques minutes.');
      } else if (error.message.includes('Configuration Stripe manquante')) {
        throw new Error('Configuration du paiement en cours. Veuillez réessayer plus tard.');
      } else if (error.message.includes('serveur backend n\'est pas accessible')) {
        throw error; // Déjà formaté correctement
      } else {
        throw error;
      }
    }
  }

  // Mettre à jour le stock après un paiement réussi
  async updateStock(purchasedItems) {
    try {
      console.log(`📦 Mise à jour du stock pour ${purchasedItems.length} produit(s)...`);
      console.log(`🌐 URL: ${BACKEND_URL}/api/update-stock`);
      
      const response = await fetch(`${BACKEND_URL}/api/update-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ purchasedItems }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue');
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ Stock mis à jour avec succès:`, data);
      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du stock:', error);
      throw error;
    }
  }

  // Récupérer le stock à jour depuis le serveur
  async getProductsStock() {
    try {
      console.log(`📦 Récupération du stock depuis le serveur...`);
      console.log(`🌐 URL: ${BACKEND_URL}/api/products-stock`);
      
      const response = await fetch(`${BACKEND_URL}/api/products-stock`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue');
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ Stock récupéré avec succès`);
      return data.products || {};
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du stock:', error);
      // Ne pas bloquer si le serveur n'est pas accessible
      return {};
    }
  }

  // Synchroniser le stock initial avec le serveur
  async syncProductsStock(products) {
    try {
      console.log(`🔄 Synchronisation du stock initial avec le serveur...`);
      console.log(`🌐 URL: ${BACKEND_URL}/api/sync-products-stock`);
      
      const response = await fetch(`${BACKEND_URL}/api/sync-products-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ products }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue');
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`✅ Stock initial synchronisé`);
      return data.products || {};
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation du stock:', error);
      // Ne pas bloquer si le serveur n'est pas accessible
      return {};
    }
  }

  // Récupérer les détails d'une session
  async getCheckoutSession(sessionId) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkout-session/${sessionId}`);
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la session:', error);
      throw error;
    }
  }

  // Supprimer un produit sur Stripe
  async deleteProduct(productId) {
    try {
      console.log(`🗑️ Suppression du produit ${productId} sur Stripe...`);
      
      const response = await fetch(`${BACKEND_URL}/api/delete-product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ productId }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Produit supprimé sur Stripe`);
      
      return data;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du produit:', error);
      throw error;
    }
  }
}

const stripeServiceInstance = new StripeService();
export default stripeServiceInstance;

